import type { ReactElement } from "react"
import { useRef, useState } from "react"
import { IconCopyFill18 } from "nucleo-ui-fill-18"
import {
	IconBellOutline18,
	IconBellSlashOutline18,
	IconCopyOutline18,
	IconEnvelopeOpenOutline18,
	IconEnvelopeOutline18,
	IconSquareArrowUpRightOutline18,
	IconTrashOutline18,
} from "nucleo-ui-outline-18"

import type {
	NotificationTarget,
	SubscribableEntityType,
} from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
} from "@productlane/ui"

import type { NotificationGroup } from "./group-notifications"
import type { NotificationRowThread } from "./notification-presentation"
import type { NotificationListRow } from "./use-notifications"
import type { CopyOption } from "@/components/thread-context-menu/thread-menu-model"
import { useSubscriptionToggle } from "@/components/notifications/subscribe-button"
import {
	handleMenuShortcut,
	threadCopyOptions,
} from "@/components/thread-context-menu/thread-menu-model"
import {
	CopyContent,
	LazySub,
} from "@/components/thread-context-menu/thread-menu-submenus"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { NOTIFICATIONS_COPY as copy } from "./copy"
import {
	notificationGroupPath,
	notificationText,
} from "./notification-presentation"
import {
	notificationTargetPath,
	readNotificationTarget,
} from "./target-to-path"

type Group = NotificationGroup<NotificationListRow>

/** The entity a notification's Subscribe follows, or null when it points at no followable thing. */
export function subscriptionEntity(
	target: NotificationTarget,
): { entityType: SubscribableEntityType; entityId: string } | null {
	switch (target.kind) {
		case "thread":
			return { entityType: "thread", entityId: target.threadId }
		case "issue":
			return { entityType: "issue", entityId: target.issueId }
		case "project":
			return { entityType: "project", entityId: target.projectId }
		case "docsArticle":
			return { entityType: "docsArticle", entityId: target.articleId }
		default:
			return null
	}
}

/** Where "Open in new tab" and "Copy link" point: the notification's target, else its inbox detail. */
export function notificationLinkPath(payload: unknown, groupKey: string) {
	return (
		notificationTargetPath(readNotificationTarget(payload)) ??
		notificationGroupPath(groupKey)
	)
}

/** Copy choices for one notification: its target's link, and the thread's details when it has one. */
export function notificationCopyOptions(
	row: {
		type: string
		payload: unknown
		thread?: (NotificationRowThread & { id?: string }) | null
	},
	{ origin, groupKey }: { origin: string; groupKey: string },
): CopyOption[] {
	const target = readNotificationTarget(row.payload)
	const link = `${origin}${notificationLinkPath(row.payload, groupKey)}`
	if (target.kind === "thread") {
		return threadCopyOptions(
			{
				id: target.threadId,
				number: row.thread?.number,
				title: row.thread?.title,
				contact: row.thread?.contact,
			},
			{ origin, link },
		)
	}
	const options: CopyOption[] = [
		{ key: "link", label: "Copy link", value: link, copied: "Link copied" },
	]
	const title = notificationText(row).title.trim()
	if (title) {
		options.push({
			key: "title",
			label: "Copy title",
			value: title,
			copied: "Title copied",
		})
	}
	return options
}

async function copyToClipboard(option: CopyOption) {
	analytics.track("notification-context-menu", { action: `copy-${option.key}` })
	try {
		await navigator.clipboard.writeText(option.value)
		toast.success({ title: option.copied, icon: IconCopyFill18 })
	} catch {
		toast.error({ title: "Couldn't copy to the clipboard" })
	}
}

interface NotificationListContextMenuProps {
	/** The list element; each row carries `data-notification-group`. */
	children: ReactElement
	getGroup: (key: string) => Group | undefined
	onToggleRead: (group: Group) => void
	onDelete: (group: Group) => void
}

/** Linear's notification menu: read state, delete and subscribe, then copy. One menu serves every row. */
export function NotificationListContextMenu({
	children,
	getGroup,
	onToggleRead,
	onDelete,
}: NotificationListContextMenuProps) {
	const [targetKey, setTargetKey] = useState<string | null>(null)
	// Deleting from the menu drops the row before the menu finishes closing.
	const lastSeen = useRef<Group | null>(null)
	const live = targetKey ? getGroup(targetKey) : undefined
	if (live) lastSeen.current = live
	const group = targetKey ? (live ?? lastSeen.current) : null

	return (
		<ContextMenu>
			<ContextMenuTrigger
				asChild
				onContextMenu={(event) => {
					const row =
						event.target instanceof Element
							? event.target.closest<HTMLElement>("[data-notification-group]")
							: null
					const key = row?.dataset.notificationGroup
					const found = key ? getGroup(key) : undefined
					if (!key || !found) {
						event.preventDefault()
						return
					}
					lastSeen.current = found
					setTargetKey(key)
				}}
			>
				{children}
			</ContextMenuTrigger>
			{group && (
				<NotificationMenuContent
					key={group.key}
					group={group}
					onToggleRead={onToggleRead}
					onDelete={onDelete}
				/>
			)}
		</ContextMenu>
	)
}

export function NotificationMenuContent({
	group,
	onToggleRead,
	onDelete,
}: {
	group: Group
	onToggleRead: (group: Group) => void
	onDelete: (group: Group) => void
}) {
	const latest = group.rows[0]
	if (!latest) return null
	const isUnread = group.unreadIds.length > 0
	const entity = subscriptionEntity(readNotificationTarget(latest.payload))
	const copyOptions = notificationCopyOptions(latest, {
		origin: window.location.origin,
		groupKey: group.key,
	})

	return (
		<ContextMenuContent
			className="w-60"
			data-testid="notification-context-menu"
			onKeyDown={handleMenuShortcut}
		>
			<ContextMenuItem
				Icon={isUnread ? IconEnvelopeOpenOutline18 : IconEnvelopeOutline18}
				shortcut={ShortcutSymbols.U}
				data-shortcut="u"
				onSelect={() => onToggleRead(group)}
			>
				{isUnread ? copy.actions.markRead : copy.actions.markUnread}
			</ContextMenuItem>
			<ContextMenuItem
				Icon={IconTrashOutline18}
				shortcut={ShortcutSymbols.BACKSPACE}
				data-shortcut="backspace"
				onSelect={() => onDelete(group)}
			>
				{copy.actions.deleteNotification}
			</ContextMenuItem>
			{entity && <SubscribeItem {...entity} />}
			<ContextMenuSeparator />
			<LazySub label={copy.menu.copy} trigger={{ Icon: IconCopyOutline18 }}>
				{() => <CopyContent options={copyOptions} onCopy={copyToClipboard} />}
			</LazySub>
			<ContextMenuItem
				Icon={IconSquareArrowUpRightOutline18}
				onSelect={() =>
					window.open(
						notificationLinkPath(latest.payload, group.key),
						"_blank",
						"noopener",
					)
				}
			>
				{copy.actions.openInNewTab}
			</ContextMenuItem>
		</ContextMenuContent>
	)
}

function SubscribeItem({
	entityType,
	entityId,
}: {
	entityType: SubscribableEntityType
	entityId: string
}) {
	const { isSubscribed, toggle } = useSubscriptionToggle(entityType, entityId)
	return (
		<ContextMenuItem
			Icon={isSubscribed ? IconBellSlashOutline18 : IconBellOutline18}
			onSelect={() => void toggle()}
		>
			{isSubscribed ? copy.subscribe.unsubscribe : copy.subscribe.subscribe}
		</ContextMenuItem>
	)
}
